import {isNil, isNotNil} from 'ramda';

import type {CustomerMessageResponseBody} from '@dms/api/core';
import type {InterestActivityResponseBody} from '@dms/api/sales';

import {
  ASSISTANT_INTEREST_EXCERPT_MAX_EVENTS,
  ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS,
  type AssistantInterestContext,
  type InterestFacts,
} from '@prompt-bridge/shared/ai-assistant-core';

import type {Nullish} from 'shared/web';

import {toAssistantInterestExcerpt} from './toAssistantInterestExcerpt';
import {toBoundedAssistantInterestText} from './toBoundedAssistantInterestText';

/** Untrusted text of a single timeline event, keyed by that event's id. */
export type AssistantInterestMessageText = {
  subject?: string | null;
  body?: string | null;
};

export const toAssistantInterestActivityMessages = (
  activities: readonly InterestActivityResponseBody[] | Nullish
): Map<string, AssistantInterestMessageText> =>
  new Map(
    (activities ?? []).map((activity) => [
      activity.id,
      {
        subject: activity.relatedMessage?.subject ?? activity.subject,
        body: activity.relatedMessage?.body ?? activity.description,
      },
    ])
  );

/** Customer messages are keyed by message id, which is also the id of their timeline event. */
export const toAssistantInterestCustomerMessages = (
  messages: readonly CustomerMessageResponseBody[] | Nullish
): Map<string, AssistantInterestMessageText> =>
  new Map(
    (messages ?? []).map((message) => [
      message.id,
      {subject: message.subject, body: message.content},
    ])
  );

/**
 * Puts bounded text on the timeline the assistant reasons over. Facts alone say only that an email
 * arrived, never what it asked, so a reply draft built from them degrades to a first-contact
 * template. Newest-first order plus the excerpt cap means the events the reply must answer keep
 * their text while older ones stay metadata.
 *
 * TRUST: subjects and excerpts can be customer-, staff-, or system-authored. Treat them as
 * untrusted and bound them here.
 */
export const toAssistantInterestTimeline = (
  timeline: InterestFacts['timeline'],
  messages: ReadonlyMap<string, AssistantInterestMessageText>
): AssistantInterestContext['timeline'] => {
  let excerptEventCount = 0;

  return timeline.map((event) => {
    const message = messages.get(event.id);
    const subject = toBoundedAssistantInterestText(message?.subject);
    const body = message?.body;

    if (isNil(body) || excerptEventCount >= ASSISTANT_INTEREST_EXCERPT_MAX_EVENTS) {
      return {
        ...event,
        id: event.id.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
        ...(isNotNil(subject) ? {subject} : {}),
      };
    }

    const excerpt = toAssistantInterestExcerpt(body);
    if (isNotNil(excerpt)) {
      excerptEventCount += 1;
    }

    return {
      ...event,
      id: event.id.slice(0, ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
      ...(isNotNil(subject) ? {subject} : {}),
      ...(isNotNil(excerpt) ? {excerpt: excerpt.text, excerptTruncated: excerpt.truncated} : {}),
    };
  });
};
